/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from "react";
import { createContext } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router";
import { generatePath } from "react-router-dom";
import Logger from "~/client/logger";
import ProjectContextRepository from "~/client/repositories/projectContextRepository";
import type { ProjectResource, ProjectSummary, VcsBranchResource, GitPersistenceSettings } from "~/client/resources";
import { HasVersionControlledPersistenceSettings } from "~/client/resources";
import { repository, client } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { useRequiredContext } from "~/hooks";
import { RecentProjects } from "~/utils/RecentProjects/RecentProjects";
import type { BranchSpecifier } from "../components/ProjectsRoutes/BranchSpecifier";
import { ShouldUseDefaultBranch, UseDefaultBranch } from "../components/ProjectsRoutes/BranchSpecifier";
import { lastAccessedBranch } from "./LastAccessedBranch";

type ProjectContextProviderProps = {
    doBusyTask: DoBusyTask;
    projectIdOrSlug: string;
    branch: BranchSpecifier;
    children: (props: ProjectContextProps) => React.ReactNode;
};

export interface BranchError {
    branchName: string;
    errors: string[];
}

export interface ProjectContextState {
    model: Readonly<ProjectResource>;
    summary: Readonly<ProjectSummary>;
    branch: Readonly<VcsBranchResource> | undefined;
    projectContextRepository: ProjectContextRepository;
    branchError: BranchError | undefined;
    isDefaultBranch: boolean | undefined;
    vcsHasError?: Boolean;
}

export interface ProjectContextActions extends ProjectLayoutActions {
    refreshModel: () => Promise<boolean>;
}

export type ProjectContextProps = ReturnType<typeof useProjectLayoutSetup>;
export const ProjectContext = createContext<ProjectContextProps | undefined>(undefined);

function modifyStateForWhenBranchNotLoaded(value: ProjectContextProps, branch: BranchSpecifier): ProjectContextProps {
    // Switching branches, we load the new branch resource asynchronously whereas the page (e.g. list of runbooks) tries
    // to reload its data. As the branch has not yet been loaded, runbooks get loaded from the old branch. We explicitly
    // set the branch to undefined here to force ProjectLayout to not render content till the branch data has been loaded.
    if (branch !== UseDefaultBranch && branch !== value.state.branch?.Name) {
        return {
            ...value,
            state: {
                ...value.state,
                branch: undefined,
            },
        };
    }
    return value;
}

const ProjectContextProvider: React.FC<ProjectContextProviderProps> = (props) => {
    const value = useProjectLayoutSetup(props.doBusyTask, props.projectIdOrSlug, props.branch);
    return <ProjectContext.Provider value={value}>{props.children(modifyStateForWhenBranchNotLoaded(value, props.branch))}</ProjectContext.Provider>;
};

export const useProjectContext = () => {
    return useRequiredContext(ProjectContext, "Project");
};

export const useOptionalProjectContext = () => {
    return React.useContext(ProjectContext);
};

const getStateUpdaters = (setState: React.Dispatch<React.SetStateAction<ProjectContextState>>) => {
    return {
        onProjectUpdated: async (project: ProjectResource, branch: VcsBranchResource | string | undefined) => {
            try {
                const summary = await repository.Projects.getSummary(project, branch);
                setState((current) => ({ ...current, model: project, summary, isDefaultBranch: IsDefaultBranch(project, branch) }));
            } catch (ex) {
                console.error(ex.ErrorMessage);
                const emptySummary = { HasRunbooks: false, HasDeploymentProcess: false };
                setState((current) => ({ ...current, model: project, summary: emptySummary, isDefaultBranch: IsDefaultBranch(project, branch) }));
            }
        },
        onVersionControlEnabled: async (project: ProjectResource) => {
            if (!HasVersionControlledPersistenceSettings(project.PersistenceSettings)) throw new Error("Config as Code: Trying to access a VCS Property on a non-VCS Project.");
            await lastAccessedBranch.save(project, project.PersistenceSettings.DefaultBranch);
            const branch = await repository.Projects.getBranch(project, project.PersistenceSettings.DefaultBranch);
            setState((current) => ({ ...current, branch: branch, projectContextRepository: new ProjectContextRepository(client, project, branch), isDefaultBranch: IsDefaultBranch(project, branch) }));
        },
        onBranchSelected: async (project: ProjectResource, branchName: string) => {
            await lastAccessedBranch.save(project, branchName);
            const branch = await repository.Projects.getBranch(project, branchName);
            setState((current) => ({ ...current, branch: branch, projectContextRepository: new ProjectContextRepository(client, project, branch), isDefaultBranch: IsDefaultBranch(project, branch) }));
        },
    };
};

function GetBranchDetails(branch: VcsBranchResource | string | undefined): string | undefined {
    if (typeof branch === "string") {
        return branch;
    } else {
        return branch?.Name;
    }
}

const IsDefaultBranch = (project: ProjectResource, branch: VcsBranchResource | string | undefined): boolean | undefined => {
    if (!project) {
        return undefined;
    }
    const persistenceSettings = project.PersistenceSettings;
    if (HasVersionControlledPersistenceSettings(persistenceSettings)) {
        return persistenceSettings.DefaultBranch == GetBranchDetails(branch);
    }
    return undefined;
};

const useProjectLayoutState = () => {
    return React.useState<ProjectContextState>({
        model: null!,
        summary: null!,
        projectContextRepository: null!,
        branch: undefined,
        branchError: undefined,
        isDefaultBranch: undefined,
    });
};

const invokeBusyWithResponse = async <T extends unknown>(action: () => Promise<T>, doBusyTask: DoBusyTask) => {
    const result = action();
    await doBusyTask(() => result);
    return result;
};

const useSaveRecentAccessedProjectIdEffect = (projectId: string | null) => {
    React.useEffect(() => {
        if (projectId) {
            RecentProjects.getInstance().UpdateAccessedProjectIntoLocalStorage(projectId).catch(Logger.log);
        }
    }, [projectId]);
};

type ProjectLayoutActions = ReturnType<typeof getStateUpdaters>;

const subscribeKey = "ProjectContextState";

const useProjectLayoutSetup = (doBusyTask: DoBusyTask, projectIdOrSlug: string, branchSpecifier: BranchSpecifier) => {
    const [state, setState] = useProjectLayoutState();
    const updaters = React.useMemo(() => getStateUpdaters(setState), [setState]);

    const match = useRouteMatch()!;
    const location = useLocation();
    const history = useHistory();

    const canChangeBranch = React.useCallback(() => match.params.hasOwnProperty("branchName"), [match]);

    const changeBranch = React.useCallback(
        (branchName: string) => {
            if (!canChangeBranch()) return;

            const newUrl = generatePath(match.path, { ...match.params, branchName });
            const newPath = newUrl + location.pathname.substr(match.url.length);

            // If the path hasn't changed, then don't bother redirecting, we might end up in a redirect loop
            if (newPath === location.pathname) return;

            history.push({ ...location, pathname: newPath });
        },
        [canChangeBranch, match, location, history]
    );

    const handleBranchError = React.useCallback(
        (project: ProjectResource, branchName: string, errors: string[]) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const vcsSettings = project.PersistenceSettings as GitPersistenceSettings;
            setState((current) => ({
                ...current,
                branchError: {
                    branchName,
                    errors,
                },
            }));

            // Todo @team-config-as-code: What if the error is related to the default branch?
            changeBranch(vcsSettings.DefaultBranch);
        },
        [changeBranch, setState]
    );

    const ensureBranchExists = React.useCallback(
        async (project: ProjectResource, branchName: string): Promise<boolean> =>
            await invokeBusyWithResponse(async (): Promise<boolean> => {
                const branches = await repository.Projects.searchBranches(project, branchName);
                const branch = branches.Items.find((b) => b.Name == branchName);
                if (branch) return true;

                handleBranchError(project, branchName, [`The branch "${branchName}" was not found.`]);
                return false;
            }, doBusyTask),
        [doBusyTask, handleBranchError]
    );

    const ensureBranchIsValid = React.useCallback(
        async (project: ProjectResource, branch: VcsBranchResource): Promise<boolean> => {
            const validationResult = await repository.Projects.validateGitRef(project, branch.Name);

            if (validationResult.Errors.length > 0) {
                handleBranchError(project, branch.Name, validationResult.Errors);
                return false;
            }

            return true;
        },
        [handleBranchError]
    );

    React.useEffect(() => {
        // eslint-disable-next-line: no-floating-promises
        if (state.model && state.model.IsVersionControlled && state.branch) {
            ensureBranchExists(state.model, state.branch.Name);
            ensureBranchIsValid(state.model, state.branch);
        }
    }, [ensureBranchExists, ensureBranchIsValid, state.model, state.branch]);

    const refreshAndGetModel = React.useCallback(
        () =>
            invokeBusyWithResponse(async () => {
                const project = await repository.Projects.get(projectIdOrSlug);
                let branchResource: VcsBranchResource | undefined = undefined;

                if (project.IsVersionControlled) {
                    try {
                        if (ShouldUseDefaultBranch(branchSpecifier)) {
                            branchResource = await lastAccessedBranch.get(project);
                        } else {
                            const branchExists = await ensureBranchExists(project, branchSpecifier);
                            if (!branchExists) {
                                return project;
                            }

                            branchResource = await repository.Projects.getBranch(project, branchSpecifier);
                            const branchIsValid = await ensureBranchIsValid(project, branchResource);
                            if (!branchIsValid) {
                                return project;
                            }

                            await lastAccessedBranch.save(project, branchSpecifier);
                        }
                    } catch {
                        setState((current) => ({
                            ...current,
                            branch: undefined,
                            vcsHasError: true,
                            projectContextRepository: new ProjectContextRepository(client, project, branchResource),
                        }));
                    }
                }

                setState((current) => ({
                    ...current,
                    branch: branchResource,
                    projectContextRepository: new ProjectContextRepository(client, project, branchResource),
                }));

                await updaters.onProjectUpdated(project, branchResource);
                return project;
            }, doBusyTask),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [doBusyTask, projectIdOrSlug, setState, updaters, branchSpecifier]
    );

    const refreshModel = React.useCallback(() => refreshAndGetModel().then((x) => true), [refreshAndGetModel]);

    React.useEffect(() => {
        repository.Projects.subscribeToDataModifications(subscribeKey, (project) => {
            setState((previous) => {
                if (previous.model && previous.model.Id === project.Id) {
                    return { ...previous, model: project };
                } else {
                    return previous;
                }
            });
        });

        return () => {
            repository.Projects.unsubscribeFromDataModifications(subscribeKey);
        };
    }, [setState]);

    React.useEffect(() => {
        // eslint-disable-next-line: no-floating-promises
        refreshAndGetModel();
    }, [refreshAndGetModel]);

    useSaveRecentAccessedProjectIdEffect(state.model && state.model.Id);

    const supportedActions = {
        refreshAndGetModel,
        refreshModel,
        changeBranch,
        ...updaters,
    };

    return {
        actions: supportedActions,
        state,
        setState,
    };
};

type ProjectContextConsumerProps = Parameters<typeof ProjectContext.Consumer>[0];
const ProjectContextConsumer: React.SFC<ProjectContextConsumerProps> = ({ children }) => {
    const context = useProjectContext();
    return <React.Fragment>{children(context)}</React.Fragment>;
};

export { ProjectContextProvider, ProjectContextConsumer };
